/**
 * Auswahlen, die eine Neuladung überstehen.
 *
 * Zwei Arten von Auswahl, zwei verschiedene Orte — und die Unterscheidung ist
 * keine Geschmacksfrage:
 *
 * - **Was zur Adresse gehört, steht in der Adresse.** Welcher Reiter offen
 *   ist, sagt, *was man ansieht*. Es gehört in die Suchparameter, weil ein
 *   solcher Verweis geteilt werden kann und der Zurück-Knopf ihn kennt. Dafür
 *   ist diese Datei nicht zuständig; das kann der Router selbst.
 * - **Was zur Person gehört, steht im Webspeicher.** Wie viele Einträge man je
 *   Seite sehen will, sagt nichts über die Sache und alles über den Menschen
 *   davor. In der Adresse wäre es Lärm: Jeder geteilte Verweis trüge die
 *   Sehgewohnheit seines Absenders mit.
 *
 * Der Speicher ist dieselbe Schublade wie bei der Projektauswahl — Präfix
 * `cubic-studio:`, jeder Zugriff in `try`, denn ein gesperrter Webspeicher
 * (privates Fenster, strenge Richtlinie) darf die Oberfläche nicht anhalten.
 * Ohne Speicher gilt der Standard, und die Auswahl hält immerhin die Sitzung.
 *
 * Gelesen wird **geprüft**: Nur ein Wert aus der erlaubten Liste kommt durch.
 * Sonst genügte ein `localStorage.setItem`, um eine Seitengröße von 10.000
 * Zeilen zu erzwingen — oder ein alter Wert aus einer früheren Fassung
 * blockierte die Liste stumm.
 */

import { useCallback, useState } from "react";

const PRAEFIX = "cubic-studio:";

/** Prüft einen rohen Speicherwert gegen die erlaubten Werte. */
export function gemerkterWert<T extends string | number>(
  roh: string | null,
  erlaubt: readonly T[],
  standard: T,
): T {
  if (roh === null) return standard;
  const treffer = erlaubt.find((wert) => String(wert) === roh);
  return treffer ?? standard;
}

function lies(schluessel: string): string | null {
  try {
    return globalThis.localStorage?.getItem(PRAEFIX + schluessel) ?? null;
  } catch {
    return null;
  }
}

function schreibe(schluessel: string, wert: string): void {
  try {
    globalThis.localStorage?.setItem(PRAEFIX + schluessel, wert);
  } catch {
    // Ohne Webspeicher hält die Auswahl die laufende Sitzung. Das genügt.
  }
}

/**
 * Wie `useState`, nur dass der Wert die Neuladung übersteht.
 *
 * Der Anfangswert wird einmal gelesen (Lazy-Initialisierung) — sonst liefe bei
 * jeder Zeichnung ein Speicherzugriff mit.
 */
export function useGemerkteAuswahl<T extends string | number>(
  schluessel: string,
  erlaubt: readonly T[],
  standard: T,
): [T, (wert: T) => void] {
  const [wert, setzeWert] = useState<T>(() =>
    gemerkterWert(lies(schluessel), erlaubt, standard),
  );

  const setze = useCallback(
    (naechster: T) => {
      schreibe(schluessel, String(naechster));
      setzeWert(naechster);
    },
    [schluessel],
  );

  return [wert, setze];
}
